import React, { useEffect, useState } from 'react';
import { Row, Col, ModalBody, Modal, ModalHeader, Form, FormGroup } from 'reactstrap';
import Swal from 'sweetalert2';
import axios from 'axios';

import { url } from '../../../../store/initialStatus';
import "./Home.css";

interface formData {
  student_id: string;
  fullname: string;

  // 1) Genetic diseases
  diabetes: string;
  asthma: string;
  epilpsy: string;
  other1: boolean;
  other1text: string;

  // 2) Impairments
  hearing: string;
  vision: string;
  speech: string;
  writing: string;

  // 3) - 6) Allergies, diet, operations, special care
  q3text: string;
  other3: boolean;
  q3: string;
  q4: string;
  q5: string;
  q6: string;

  // 7) Emergency contacts
  emergencyname1: string;
  emergencyrelationship1: string;
  emergencymobile1: string;
  emergencyname2: string;
  emergencyrelationship2: string;
  emergencymobile2: string;
}

/**
 * Mandatory medical record form.
 *
 * On mount, asks the backend for the next student without a medical record.
 * If there is one, the modal opens; after saving it checks again, so the
 * guardian is walked through every student in turn.
 */
const MedicalRecord = () => {
  // Keeps checkboxes / radios visible regardless of theme overrides
  const styles = {
    display: "block",
    opacity: "1"
  };

  const [modal, setModal] = useState(false);
  const [formData, setFormData] = useState<formData>({
    student_id: "",
    fullname: "",
    diabetes: "",
    asthma: "",
    epilpsy: "",
    hearing: "",
    speech: "",
    vision: "",
    writing: "",
    other1: false,
    other1text: "",
    other3: false,
    q3text: "",
    q3: "",
    q4: "",
    q5: "",
    q6: "",
    emergencymobile1: "",
    emergencymobile2: "",
    emergencyname1: "",
    emergencyname2: "",
    emergencyrelationship1: "",
    emergencyrelationship2: ""
  });

  const clearState = () => {
    setFormData({
      student_id: "",
      fullname: "",
      diabetes: "",
      asthma: "",
      epilpsy: "",
      hearing: "",
      speech: "",
      vision: "",
      writing: "",
      other1: false,
      other3: false,
      other1text: "",
      q3text: "",
      q3: "",
      q4: "",
      q5: "",
      q6: "",
      emergencymobile1: "",
      emergencymobile2: "",
      emergencyname1: "",
      emergencyname2: "",
      emergencyrelationship1: "",
      emergencyrelationship2: ""
    });
    setModal(false);
  };

  // Loads the next student that still needs a medical record
  const getStudent = () => {
    axios.post(`${url}student/medicalrecord/getstudent.php`).then((res: any) => {
      if (res.data.data.student_id) {
        setFormData(prevState => ({ ...prevState, student_id: res.data.data.student_id, fullname: res.data.data.fullname }));
        setModal(true);
      }
    });
  };

  useEffect(() => {
    (async () => {
      getStudent();
    })();
  }, []);

  const submitHandler = (e: any) => {
    e.preventDefault();
    Swal.showLoading();
    axios.post(`${url}student/medicalrecord/savemedicalrecord.php`, formData).then((res: any) => {
      if (res.data.status === "success") {
        Swal.fire("Saved Successfully!", "", "success").then(() => {
          clearState();
          getStudent();
        });
      } else {
        Swal.fire("Something go wrong please try again later !!", "", "error");
      }
    });
  };

  const handleChange = (e: any) => {
    const name = e.target.name;
    const value = e.target.value;
    setFormData(prevState => ({ ...prevState, [name]: value }));
  };

  return (
    <Modal isOpen={modal} size="lg" scrollable className="pp-form-modal">
      <ModalHeader>Medical record for student : {formData.fullname}</ModalHeader>
      <ModalBody>
        <p className="pp-form-intro">
          Please complete this form so the school clinic can care for your child. Fields marked with{" "}
          <span className="pp-required">*</span> are required.
        </p>

        <Form onSubmit={submitHandler}>
          {/* ---------------- Health conditions ---------------- */}
          <div className="pp-form-section">
            <h3 className="pp-form-section-title">
              <i className="fas fa-notes-medical" aria-hidden="true"></i> Health conditions
            </h3>

            <FormGroup>
              <label className="pp-form-question">
                <span className="pp-form-question-number">1)</span>
                Is your child suffering from any genetic diseases?
              </label>
              <div className="pp-choice-group">
                <div className="form-check">
                  <input style={styles} className="form-check-input" type="checkbox" name="diabetes" id="diabetes" value="diabetes" checked={formData.diabetes ? true : false} onChange={handleChange} />
                  <label className="form-check-label" htmlFor="diabetes">Diabetes</label>
                </div>
                <div className="form-check">
                  <input style={styles} className="form-check-input" type="checkbox" name="asthma" id="asthma" value="asthma" checked={formData.asthma ? true : false} onChange={handleChange} />
                  <label className="form-check-label" htmlFor="asthma">Asthma</label>
                </div>
                <div className="form-check">
                  <input style={styles} className="form-check-input" type="checkbox" name="epilpsy" id="epilpsy" value="epilpsy" checked={formData.epilpsy ? true : false} onChange={handleChange} />
                  <label className="form-check-label" htmlFor="epilpsy">Epilepsy</label>
                </div>
                <div className="form-check">
                  <input
                    style={styles}
                    className="form-check-input"
                    type="checkbox"
                    name="other1"
                    id="other1"
                    value="other1"
                    checked={formData.other1 ? true : false}
                    onChange={(e: any) => {
                      handleChange(e);
                      const temp = e.target.checked ? true : false;
                      setFormData(prevState => ({ ...prevState, other1: temp }));
                    }}
                  />
                  <label className="form-check-label" htmlFor="other1">Other</label>
                </div>
              </div>
              {formData.other1 ? (
                <div className="mt-2">
                  <label className="pp-form-label" htmlFor="other1text">If other write it here</label>
                  <textarea className="form-control" id="other1text" name="other1text" rows={3} value={formData.other1text} onChange={handleChange}></textarea>
                </div>
              ) : null}
            </FormGroup>

            <FormGroup className="mb-0">
              <label className="pp-form-question">
                <span className="pp-form-question-number">2)</span>
                Is your child suffering from any of the following impairment?
              </label>
              <div className="pp-choice-group">
                <div className="form-check">
                  <input style={styles} className="form-check-input" type="checkbox" name="hearing" id="hearing" value="hearing" checked={formData.hearing ? true : false} onChange={handleChange} />
                  <label className="form-check-label" htmlFor="hearing">Hearing</label>
                </div>
                <div className="form-check">
                  <input style={styles} className="form-check-input" type="checkbox" name="vision" id="vision" value="vision" checked={formData.vision ? true : false} onChange={handleChange} />
                  <label className="form-check-label" htmlFor="vision">Vision</label>
                </div>
                <div className="form-check">
                  <input style={styles} className="form-check-input" type="checkbox" name="speech" id="speech" value="speech" checked={formData.speech ? true : false} onChange={handleChange} />
                  <label className="form-check-label" htmlFor="speech">Speech</label>
                </div>
                <div className="form-check">
                  <input style={styles} className="form-check-input" type="checkbox" name="writing" id="writing" value="writing" checked={formData.writing ? true : false} onChange={handleChange} />
                  <label className="form-check-label" htmlFor="writing">Writing</label>
                </div>
              </div>
            </FormGroup>
          </div>

          {/* ---------------- Allergies, diet & history ---------------- */}
          <div className="pp-form-section">
            <h3 className="pp-form-section-title">
              <i className="fas fa-allergies" aria-hidden="true"></i> Allergies, diet &amp; history
            </h3>

            <FormGroup>
              <label className="pp-form-question">
                <span className="pp-form-question-number">3)</span>
                Is your child has allergy to medicine or other? <span className="pp-required">*</span>
              </label>
              <div className="pp-choice-group">
                <div className="form-check">
                  <input
                    style={styles}
                    className="form-check-input"
                    type="radio"
                    required
                    name="q3"
                    id="yes3"
                    value="yes3"
                    checked={formData.q3 == "yes3" ? true : false}
                    onChange={(e) => {
                      handleChange(e);
                      setFormData(prevState => ({ ...prevState, other3: false }));
                    }}
                  />
                  <label className="form-check-label" htmlFor="yes3">Yes</label>
                </div>
                <div className="form-check">
                  <input
                    style={styles}
                    className="form-check-input"
                    type="radio"
                    required
                    name="q3"
                    id="no3"
                    value="no3"
                    checked={formData.q3 == "no3" ? true : false}
                    onChange={(e) => {
                      handleChange(e);
                      setFormData(prevState => ({ ...prevState, other3: false }));
                    }}
                  />
                  <label className="form-check-label" htmlFor="no3">No</label>
                </div>
                <div className="form-check">
                  <input
                    style={styles}
                    className="form-check-input"
                    type="radio"
                    required
                    name="q3"
                    id="other3"
                    value="other3"
                    checked={formData.q3 == "other3" ? true : false}
                    onChange={(e) => {
                      handleChange(e);
                      const temp = e.target.checked ? true : false;
                      setFormData(prevState => ({ ...prevState, other3: temp }));
                    }}
                  />
                  <label className="form-check-label" htmlFor="other3">Other</label>
                </div>
              </div>
              {formData.other3 ? (
                <div className="mt-2">
                  <label className="pp-form-label" htmlFor="q3text">If other write it here</label>
                  <textarea className="form-control" id="q3text" name="q3text" value={formData.q3text} rows={3} onChange={handleChange}></textarea>
                </div>
              ) : null}
            </FormGroup>

            <FormGroup>
              <label className="pp-form-question">
                <span className="pp-form-question-number">4)</span>
                Is there any dietary restrications? <span className="pp-required">*</span>
              </label>
              <div className="pp-choice-group">
                <div className="form-check">
                  <input style={styles} className="form-check-input" type="radio" required name="q4" id="yes4" value="yes4" checked={formData.q4 == "yes4" ? true : false} onChange={handleChange} />
                  <label className="form-check-label" htmlFor="yes4">Yes</label>
                </div>
                <div className="form-check">
                  <input style={styles} className="form-check-input" type="radio" required name="q4" id="no4" value="no4" checked={formData.q4 == "no4" ? true : false} onChange={handleChange} />
                  <label className="form-check-label" htmlFor="no4">No</label>
                </div>
              </div>
            </FormGroup>

            <FormGroup>
              <label className="pp-form-question">
                <span className="pp-form-question-number">5)</span>
                Is there any operations done to the child? <span className="pp-required">*</span>
              </label>
              <div className="pp-choice-group">
                <div className="form-check">
                  <input style={styles} className="form-check-input" type="radio" required name="q5" id="yes5" value="yes5" checked={formData.q5 == "yes5" ? true : false} onChange={handleChange} />
                  <label className="form-check-label" htmlFor="yes5">Yes</label>
                </div>
                <div className="form-check">
                  <input style={styles} className="form-check-input" type="radio" required name="q5" id="no5" value="no5" checked={formData.q5 == "no5" ? true : false} onChange={handleChange} />
                  <label className="form-check-label" htmlFor="no5">No</label>
                </div>
              </div>
            </FormGroup>

            <FormGroup className="mb-0">
              <label className="pp-form-question" htmlFor="q6">
                <span className="pp-form-question-number">6)</span>
                Are there any special consendrations/actions required by us at school regarding health issue? <span className="pp-required">*</span>
              </label>
              <textarea className="form-control" name="q6" id="q6" required rows={3} value={formData.q6} onChange={handleChange}> </textarea>
            </FormGroup>
          </div>

          {/* ---------------- Emergency contacts ---------------- */}
          <div className="pp-form-section">
            <h3 className="pp-form-section-title">
              <i className="fas fa-phone-alt" aria-hidden="true"></i> Emergency contacts
            </h3>

            <FormGroup className="mb-0">
              <label className="pp-form-question">
                <span className="pp-form-question-number">7)</span>
                In emergency treatment for the child who become ill/ injured while under school care the number we cantacted
              </label>

              <p className="pp-form-sublabel">A) Emergency contact 1 <span className="pp-required">*</span></p>
              <Row>
                <Col md={4}>
                  <div className="form-group">
                    <label className="pp-form-label" htmlFor="emergencyname1">Name</label>
                    <input type="text" className="form-control" id="emergencyname1" required name="emergencyname1" value={formData.emergencyname1} placeholder="Enter name" onChange={handleChange} />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="form-group">
                    <label className="pp-form-label" htmlFor="emergencyrelationship1">Relationship</label>
                    <input type="text" className="form-control" id="emergencyrelationship1" required name="emergencyrelationship1" value={formData.emergencyrelationship1} placeholder="Enter relationship" onChange={handleChange} />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="form-group">
                    <label className="pp-form-label" htmlFor="emergencymobile1">Mobile Number</label>
                    <input type="text" className="form-control" id="emergencymobile1" required name="emergencymobile1" placeholder="Enter mobile" value={formData.emergencymobile1} onChange={handleChange} />
                  </div>
                </Col>
              </Row>

              <p className="pp-form-sublabel mt-3">B) Emergency contact 2</p>
              <Row>
                <Col md={4}>
                  <div className="form-group">
                    <label className="pp-form-label" htmlFor="emergencyname2">Name</label>
                    <input type="text" className="form-control" id="emergencyname2" name="emergencyname2" value={formData.emergencyname2} placeholder="Enter name" onChange={handleChange} />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="form-group">
                    <label className="pp-form-label" htmlFor="emergencyrelationship2">Relationship</label>
                    <input type="text" className="form-control" id="emergencyrelationship2" name="emergencyrelationship2" value={formData.emergencyrelationship2} placeholder="Enter relationship" onChange={handleChange} />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="form-group">
                    <label className="pp-form-label" htmlFor="emergencymobile2">Mobile Number</label>
                    <input type="text" className="form-control" id="emergencymobile2" name="emergencymobile2" placeholder="Enter mobile" value={formData.emergencymobile2} onChange={handleChange} />
                  </div>
                </Col>
              </Row>
            </FormGroup>
          </div>

          <div className="pp-form-actions">
            <button type="submit" className="btn pp-btn-submit">Submit</button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default MedicalRecord;
